import { useEffect, useRef, useState } from "react";
import ImmediateOwnerShip from "../../ImmediateOwnerShip/ImmediateOwnerShip";
import { usesVobiz } from "../../../utils/apiBackend";

/*
 * Defensive parse of agent.voip_numbers — Sequelize hands this column
 * back as either a parsed array, a JSON string, or a bare phone string
 * depending on driver state. We only care whether ANY number is set so
 * we can hide the inline picker's Find More Number link once the user
 * has a committed agent number.
 */
function hasUsableVoip(raw) {
  if (!raw) return false;
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    const first = Array.isArray(parsed) ? parsed[0] : parsed;
    return !!first;
  } catch {
    // Not JSON — bare string with content counts as a usable number.
    return typeof raw === "string" && raw.length > 0;
  }
}

// ImmediateOwnershipStep — wraps <ImmediateOwnerShip/> in the post-purchase
// wizard. The underlying screen reads agentId / businessId from
// sessionStorage, so we prime those from agentCtx. wizardMode diverts its
// two exit paths: "Set Up Call Forwarding" -> onNext (carrier step),
// "Go to Dashboard" modal -> onSkip (end wizard).

export default function ImmediateOwnershipStep({
  onNext,
  onBack,
  onSkip,
  agentCtx,
  refreshAgentCtx,
  // Fetch-only candidate flow (GoLiveWizard only — PostPurchaseSetup
  // doesn't pass these so the ownership screen renders its legacy
  // DB-voip path unchanged).
  candidate,
  isPaidPlan,
  confirming,
  confirmError,
  onConfirmPurchase,
  onEditNumber,
  // One-shot edit gate from GoLiveWizard. When true the Find More
  // Number link is hidden — user already swapped the cascade pick
  // once (or successfully Confirmed) and the screen is now read-only
  // for the assigned number.
  editConsumed = false,
  // GoLiveWizard merged Step 1 — toggles the new Figma layout with
  // inline picker, "Find More Number" link, and Benefits panel.
  // PostPurchase doesn't pass this so its flow renders unchanged.
  wizardInlinePicker = false,
  // Forwarded to ImmediateOwnership so its inline "Skip phone setup"
  // link (shown only on the US/Canada-only gate) can call back into
  // the wizard, which jumps straight to the Triage step. Both
  // GoLiveWizard and PostPurchaseSetup pass this when they support
  // the skip; otherwise the link silently doesn't render.
  onSkipPhoneSetup,
}) {
  const [ready, setReady] = useState(false);
  // Mount-time refresh — Step 4 (NumberAssign) just wrote
  // voip_number_city / voip_number_state to the agent row, but the
  // wizard's agentCtx was hydrated BEFORE that write. Without an
  // explicit refresh on Step 5 mount, agentCtx.agent.voip_number_city
  // stays NULL and the city derivation below falls back to
  // business.city — which is exactly what the user saw (typed
  // "British Columbia" on Step 4 → Step 5 still showed the business
  // HQ city). Fires once per mount via refreshedRef so a re-render
  // can't double-fetch.
  const refreshedRef = useRef(false);

  useEffect(() => {
    if (agentCtx?.loading) return;
    if (agentCtx?.agentId)
      sessionStorage.setItem("SelectAgentId", agentCtx.agentId);
    if (agentCtx?.business?.businessId)
      sessionStorage.setItem(
        "SelectAgentBusinessId",
        agentCtx.business.businessId,
      );
    setReady(true);
  }, [agentCtx]);

  useEffect(() => {
    if (refreshedRef.current) return;
    if (typeof refreshAgentCtx !== "function") return;
    refreshedRef.current = true;
    refreshAgentCtx();
  }, [refreshAgentCtx]);

  if (!ready) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "#6b7280" }}>
        Loading your agent number…
      </div>
    );
  }

  // Pull personalization values off agentCtx:
  //   wizardCity — locality of the assigned/candidate number (may
  //     differ from biz city because of cascade fallback). Candidate
  //     locality wins when present (the candidate hasn't been written
  //     to the agent row yet, so voip_number_city is still empty).
  //   wizardAgentName — agent.agentName from DB; final fallback
  //     "Sophia" matches the default name used at agent creation.
  const isVobiz = usesVobiz() || agentCtx?.business?.country_code === "IN";
  const city =
    candidate?.locality ||
    agentCtx?.agent?.voip_number_city ||
    agentCtx?.business?.city ||
    (isVobiz ? "Bangalore" : (agentCtx?.business?.country_code || "US"));
  const agentName = agentCtx?.agent?.agentName || "Sophia";

  // Picker fetch needs business country/state/city. Prefer the
  // candidate's locality (it reflects what the cascade actually
  // picked, which may differ from biz HQ city for non-local
  // numbers) — falls back to business fields, finally to country.
  const businessCountry = (
    agentCtx?.business?.country_code ||
    agentCtx?.business?.country ||
    (isVobiz ? "IN" : "")
  )
    .toString()
    .toUpperCase();
  const businessCity =
    candidate?.locality ||
    agentCtx?.agent?.voip_number_city ||
    agentCtx?.business?.city ||
    (isVobiz ? "Bangalore" : "");
  const businessState =
    candidate?.region ||
    agentCtx?.agent?.voip_number_state ||
    agentCtx?.business?.state_code ||
    agentCtx?.business?.state ||
    (isVobiz ? "KA" : "");

  return (
    <ImmediateOwnerShip
      wizardMode={true}
      onWizardNext={onNext}
      onWizardBack={onBack}
      onWizardClose={onSkip}
      wizardCity={city}
      wizardAgentName={agentName}
      // Candidate flow — undefined for PostPurchase (legacy DB-voip
      // path stays untouched), populated by GoLiveWizard. The
      // underlying component branches on `wizardCandidateNumber`
      // being truthy.
      wizardCandidateNumber={candidate?.phone_number || ""}
      /*
       * In inline-picker mode (GoLiveWizard merged Step 1) the
       * Find More Number link should be visible whenever the user
       * still has a chance to swap their number — i.e. they haven't
       * consumed their one-shot edit AND nothing has been written
       * to the agent row yet (no voip on agentCtx). We DELIBERATELY
       * drop the `isPaidPlan && candidate` gate that the legacy
       * pencil-icon path used:
       *   - isPaidPlan is redundant because GoLiveWizard's plan-
       *     gate effect bounces free/demo users before we ever
       *     render here; checking it again caused the link to
       *     disappear when `agentPlan` was momentarily empty
       *     during early hydration (the bug the user reported).
       *   - Candidate may legitimately be null if the cascade
       *     failed (non-US/CA, empty pool, etc.) — in that case
       *     the user MUST be able to open the picker manually.
       *
       * For the legacy non-inline path (PostPurchase) we keep the
       * old conservative gate so its pencil icon still requires a
       * candidate to be meaningful.
       */
      wizardCanEdit={
        wizardInlinePicker
          ? !editConsumed &&
            !(agentCtx?.agent?.voip_numbers && hasUsableVoip(agentCtx.agent.voip_numbers))
          : Boolean(isPaidPlan && candidate?.phone_number && !editConsumed)
      }
      wizardConfirming={Boolean(confirming)}
      wizardConfirmError={confirmError || ""}
      onWizardConfirmPurchase={onConfirmPurchase}
      onWizardEditNumber={onEditNumber}
      wizardInlinePicker={wizardInlinePicker}
      wizardBusinessCountry={businessCountry}
      wizardBusinessCity={businessCity}
      wizardBusinessState={businessState}
      onWizardSkipPhoneSetup={onSkipPhoneSetup}
    />
  );
}
